import { useState, useEffect } from 'react';
import { detectSocialMediaUrls, getEmbedUrl, EMBED_DIMENSIONS, PLATFORM_INFO, removeSocialMediaUrls } from '../utils/socialEmbed';
import { ExternalLink, Play, Maximize2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Social Media Embed Component
const SocialEmbed = ({ url, platform, id, onVideoClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [oembedHtml, setOembedHtml] = useState(null);
  const [oembedLoading, setOembedLoading] = useState(false);
  const dimensions = EMBED_DIMENSIONS[platform];
  const info = PLATFORM_INFO[platform];

  // Check if this is a video platform
  const isVideo = ['youtube', 'tiktok'].includes(platform);
  
  // For TikTok: check if ID is numeric (full video ID) or a short code
  // Short codes from vm.tiktok.com and /t/ URLs need oEmbed API
  const isTikTokShortUrl = platform === 'tiktok' && id && !/^\d+$/.test(id);
  
  // Get embed URL - null for TikTok short URLs since they need oEmbed
  const embedUrl = isTikTokShortUrl ? null : getEmbedUrl(platform, url, id);

  // Fetch oEmbed HTML for TikTok short URLs
  useEffect(() => {
    if (isTikTokShortUrl && !oembedHtml && !oembedLoading) {
      setOembedLoading(true);
      axios.post(`${API}/oembed/fetch`, null, { params: { url } })
        .then(response => {
          if (response.data.supported && response.data.embed?.html) {
            setOembedHtml(response.data.embed.html);
          } else {
            setError(true);
          }
        })
        .catch(() => {
          setError(true);
        })
        .finally(() => {
          setOembedLoading(false);
          setLoaded(true);
        });
    }
  }, [isTikTokShortUrl, url, oembedHtml, oembedLoading]);

  // Load TikTok embed script when oEmbed HTML is set
  useEffect(() => {
    if (oembedHtml && platform === 'tiktok') {
      // Check if TikTok script is already loaded
      if (!document.querySelector('script[src*="tiktok.com/embed.js"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
      } else {
        // If script is already loaded, trigger re-processing
        if (window.tiktokEmbed) {
          window.tiktokEmbed.lib.render();
        }
      }
    }
  }, [oembedHtml, platform]);

  if (error || (!embedUrl && !isTikTokShortUrl)) {
    // Fallback link card for errors or unsupported embeds
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors"
        data-testid={`social-link-${platform}`}
      >
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${info.color}20` }}
        >
          <i className={`${info.icon} text-lg`} style={{ color: info.color }}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--ps-text-primary)]">
            View on {info.name}
          </p>
          <p className="text-xs text-[var(--ps-text-muted)] truncate">{url}</p>
        </div>
        <ExternalLink className="w-4 h-4 text-[var(--ps-text-muted)]" />
      </a>
    );
  }

  // TikTok embed - handle both direct embeds and oEmbed HTML
  if (platform === 'tiktok') {
    // For short URLs, use oEmbed HTML
    if (isTikTokShortUrl) {
      if (oembedLoading) {
        return (
          <div className="social-embed tiktok-embed" style={{ maxWidth: dimensions.maxWidth }}>
            <div className="bg-[var(--ps-bg-surface)] rounded-xl p-8 flex items-center justify-center border border-[var(--ps-border)]">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-[var(--ps-primary)]" />
                <p className="text-xs text-[var(--ps-text-muted)]">Loading TikTok...</p>
              </div>
            </div>
          </div>
        );
      }
      
      if (oembedHtml) {
        return (
          <div className="social-embed tiktok-embed relative" style={{ maxWidth: dimensions.maxWidth }}>
            <div 
              className="tiktok-oembed-container rounded-xl overflow-hidden"
              dangerouslySetInnerHTML={{ __html: oembedHtml }}
            />
            {onVideoClick && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onVideoClick({ platform, id, url, embedUrl: null });
                }}
                className="absolute top-2 right-2 z-20 p-2.5 rounded-full bg-black/80 text-white shadow-lg active:scale-95 transition-transform"
                title="Open in full screen viewer"
                data-testid="expand-video-btn"
              >
                <Maximize2 className="w-5 h-5" />
              </button>
            )}
          </div>
        );
      }
      
      // Fallback to link card if oEmbed failed
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 p-3 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors"
          data-testid={`social-link-${platform}`}
        >
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${info.color}20` }}
          >
            <i className={`${info.icon} text-lg`} style={{ color: info.color }}></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--ps-text-primary)]">
              View on {info.name}
            </p>
            <p className="text-xs text-[var(--ps-text-muted)] truncate">{url}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-[var(--ps-text-muted)]" />
        </a>
      );
    }
    
    // For full video URLs, use direct iframe embed
    return (
      <div className="social-embed tiktok-embed relative" style={{ maxWidth: dimensions.maxWidth }}>
        <div className="relative bg-black rounded-xl overflow-hidden" style={{ paddingBottom: '177%' }}>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--ps-bg-surface)]">
              <div className="text-center">
                <i className="fab fa-tiktok text-3xl mb-2" style={{ color: info.color }}></i>
                <p className="text-xs text-[var(--ps-text-muted)]">Loading TikTok...</p>
              </div>
            </div>
          )}
          <iframe
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none' }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            title="TikTok video"
          />
        </div>
        {/* Always-visible expand button - Opens video reel viewer */}
        {onVideoClick && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onVideoClick({ platform, id, url, embedUrl });
            }}
            className="absolute top-2 right-2 z-20 p-2.5 rounded-full bg-black/80 text-white shadow-lg active:scale-95 transition-transform"
            title="Open in full screen viewer"
            data-testid="expand-video-btn"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        )}
      </div>
    );
  }

  // Instagram embed
  if (platform === 'instagram') {
    return (
      <div className="social-embed instagram-embed" style={{ maxWidth: dimensions.maxWidth }}>
        <div className="relative bg-[var(--ps-bg-surface)] rounded-xl overflow-hidden border border-[var(--ps-border)]">
          {!loaded && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <i className="fab fa-instagram text-3xl mb-2" style={{ color: info.color }}></i>
                <p className="text-xs text-[var(--ps-text-muted)]">Loading Instagram...</p>
              </div>
            </div>
          )}
          <iframe
            src={embedUrl}
            className="w-full"
            style={{ border: 'none', minHeight: dimensions.height }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            title="Instagram post"
          />
        </div>
      </div>
    );
  }

  // YouTube embed - with HD quality parameters
  if (platform === 'youtube') {
    return (
      <div className="social-embed youtube-embed relative" style={{ maxWidth: dimensions.maxWidth }}>
        <div className="relative bg-black rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-[var(--ps-bg-surface)]">
              <div className="text-center">
                <Play className="w-12 h-12 text-red-500 mb-2" />
                <p className="text-xs text-[var(--ps-text-muted)]">Loading YouTube...</p>
              </div>
            </div>
          )}
          <iframe
            src={`${embedUrl}&modestbranding=1&rel=0&playsinline=1`}
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            title="YouTube video"
          />
        </div>
        {/* Always-visible expand button - Opens video reel viewer */}
        {onVideoClick && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onVideoClick({ platform, id, url, embedUrl });
            }}
            className="absolute top-2 right-2 z-20 p-2.5 rounded-full bg-black/80 text-white shadow-lg active:scale-95 transition-transform"
            title="Open in full screen viewer"
            data-testid="expand-video-btn"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        )}
      </div>
    );
  }

  // Facebook embed
  if (platform === 'facebook') {
    return (
      <div className="social-embed facebook-embed" style={{ maxWidth: dimensions.maxWidth }}>
        <div className="relative bg-[var(--ps-bg-surface)] rounded-xl overflow-hidden border border-[var(--ps-border)]">
          {!loaded && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <i className="fab fa-facebook text-3xl mb-2" style={{ color: info.color }}></i>
                <p className="text-xs text-[var(--ps-text-muted)]">Loading Facebook...</p>
              </div>
            </div>
          )}
          <iframe
            src={embedUrl}
            className="w-full"
            style={{ border: 'none', minHeight: dimensions.height }}
            allow="autoplay; encrypted-media"
            allowFullScreen
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            title="Facebook post"
          />
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-[var(--ps-bg-surface)] border border-[var(--ps-border)] rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors"
    >
      <ExternalLink className="w-5 h-5 text-[var(--ps-text-muted)]" />
      <span className="text-sm text-[var(--ps-text-primary)] truncate">{url}</span>
    </a>
  );
};

// Main component that detects and renders embeds from post content
export const PostContentWithEmbeds = ({ content, onVideoClick, postData }) => {
  const [embeds, setEmbeds] = useState([]);
  const [cleanContent, setCleanContent] = useState(content);

  useEffect(() => {
    const detectedUrls = detectSocialMediaUrls(content);
    setEmbeds(detectedUrls);
    
    // Only clean content if we found embeddable URLs
    if (detectedUrls.length > 0) {
      setCleanContent(removeSocialMediaUrls(content));
    } else {
      setCleanContent(content);
    }
  }, [content]);

  // Handle video click - pass additional post data for the reel viewer
  const handleVideoClick = (videoData) => {
    if (onVideoClick) {
      onVideoClick({
        ...videoData,
        postId: postData?.post_id,
        userName: postData?.user_name,
        content: cleanContent,
        isLiked: postData?.is_liked,
        likesCount: postData?.likes_count,
        commentsCount: postData?.comments_count,
      });
    }
  };

  return (
    <div className="post-content-with-embeds">
      {/* Post text (without the URLs that will be embedded) */}
      {cleanContent && (
        <p className="text-[var(--ps-text-primary)] whitespace-pre-wrap mb-3">
          {cleanContent}
        </p>
      )}
      
      {/* Embedded content */}
      {embeds.map((embed, index) => (
        <div key={`${embed.platform}-${embed.id}-${index}`} className="mb-3">
          <SocialEmbed
            url={embed.url}
            platform={embed.platform}
            id={embed.id}
            onVideoClick={onVideoClick ? handleVideoClick : undefined}
          />
        </div>
      ))}
    </div>
  );
};

export default SocialEmbed;
